import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppStore } from '../lib/store'
import type { User } from '../types'
import { Home, Users, Shield, Clock, Star, CheckCircle, ArrowRight, Menu, X, HelpCircle } from 'lucide-react'
import { toast } from '../lib/toast'
import AuthModal from './AuthModal'
import HelpModal from './HelpModal'
import ThemeToggle from './ThemeToggle'

export default function LandingPage() {
  const { setUser } = useAppStore()
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const [showAuthModal, setShowAuthModal] = React.useState(false)
  const [authMode, setAuthMode] = React.useState<'login' | 'signup'>('login')
  const [showHelpModal, setShowHelpModal] = React.useState(false)

  // Fixed: Single auth modal with mode switching - HMR refresh
  const openAuthModal = (mode: 'login' | 'signup') => {
    setAuthMode(mode)
    setShowAuthModal(true)
  }

  const closeAuthModal = () => {
    setShowAuthModal(false)
  }

  return (
    <div className="min-h-screen bg-white dark:bg-dark-bg">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 dark:bg-dark-card/95 backdrop-blur-sm border-b border-gray-200 dark:border-dark-border z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Shield className="w-8 h-8 text-orange-500" />
              <span className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">SmartClean</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => setShowHelpModal(true)}
                className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary transition-colors flex items-center space-x-1"
                title="Open Help Guide"
              >
                <HelpCircle className="w-4 h-4" />
                <span>Help</span>
              </button>
              <ThemeToggle />
              <button 
                onClick={() => openAuthModal('signup')}
                className="bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-800 transition-colors"
              >
                Sign Up
              </button>
            </div>

            <div className="flex items-center space-x-2 md:hidden">
              <ThemeToggle />
              <button 
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-white dark:bg-dark-card border-b border-gray-200 dark:border-dark-border">
              <div className="px-4 py-2 space-y-2">
                <button 
                  onClick={() => setShowHelpModal(true)}
                  className="w-full text-left py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors flex items-center space-x-2"
                >
                  <HelpCircle className="w-4 h-4" />
                  <span>Help</span>
                </button>
                <button 
                  onClick={() => openAuthModal('login')}
                  className="w-full bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-800 transition-colors text-left"
                >
                  Sign In
                </button>
                <button 
                  onClick={() => openAuthModal('signup')}
                  className="w-full bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-800 transition-colors text-left"
                >
                  Sign Up
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-2 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-dark-text-primary mb-3">
              Professional Cleaning
              <span className="block text-orange-500">Made Simple</span>
            </h1>
            <p className="text-sm md:text-base text-gray-600 dark:text-dark-text-secondary mb-6 max-w-2xl mx-auto">
              Trusted cleaners. Spotless results. Book in seconds.
            </p>
            
            <div className="flex justify-center gap-4 mb-6 max-w-md mx-auto">
              <button
                onClick={() => openAuthModal('signup')}
                className="flex-1 bg-green-700 text-white px-6 py-3 rounded-lg hover:bg-green-800 transition-colors text-lg font-semibold"
              >
                Get Started
              </button>
              <button
                onClick={() => openAuthModal('login')}
                className="flex-1 border-2 border-green-700 text-green-700 bg-transparent px-6 py-3 rounded-lg hover:bg-green-50 transition-colors text-lg font-semibold"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={closeAuthModal}
        mode={authMode}
        onModeChange={setAuthMode}
      />

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />

      {/* Stats & Benefits Section */}
      <section id="stats" className="py-3 px-4 sm:px-6 lg:px-8 bg-slate-100 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-2">
              <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-dark-text-primary mb-2 max-w-3xl mx-auto">Why Choose SmartClean</h2>
            </div>
            
            <div className="grid grid-cols-3 gap-2 md:gap-3 lg:gap-4 text-center">
              <div className="text-center p-2 md:p-3 bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-100 dark:border-dark-border group hover:shadow-md transition-shadow">
                <Clock className="w-6 h-6 md:w-8 md:h-8 text-blue-500 mx-auto mb-1 md:mb-2" />
                <h3 className="text-xs md:text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-1">Save Time</h3>
                <div className="text-lg sm:text-xl font-bold text-orange-500 mb-1">5,000+</div>
                <div className="text-xs text-gray-600 dark:text-gray-300">Happy Clients</div>
              </div>
              
              <div className="text-center p-2 md:p-3 bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-100 dark:border-dark-border group hover:shadow-md transition-shadow">
                <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-green-500 mx-auto mb-1 md:mb-2" />
                <h3 className="text-xs md:text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-1">Peace of Mind</h3>
                <div className="text-lg sm:text-xl font-bold text-orange-500 mb-1">500+</div>
                <div className="text-xs text-gray-600 dark:text-gray-300">Verified Cleaners</div>
              </div>
              
              <div className="text-center p-2 md:p-3 bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-100 dark:border-dark-border group hover:shadow-md transition-shadow">
                <Star className="w-6 h-6 md:w-8 md:h-8 text-yellow-500 mx-auto mb-1 md:mb-2" />
                <h3 className="text-xs md:text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-1">Consistent Quality</h3>
                <div className="text-lg sm:text-xl font-bold text-orange-500 mb-1">10,000+</div>
                <div className="text-xs text-gray-600 dark:text-gray-300">Jobs Completed</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text-primary py-2 px-4 sm:px-6 lg:px-8 border-t border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-xs sm:text-sm text-gray-600 dark:text-dark-text-secondary">
              <div className="flex justify-center items-center space-x-4">
                <span>© 2026 SmartClean. All rights reserved.</span>
                <a href="#" className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary transition-colors">Privacy</a>
                <a href="#" className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary transition-colors">Terms</a>
                <a href="#" className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary transition-colors">Contact</a>
              </div>
              
              <div className="flex justify-center items-center space-x-4">
                <span className="text-xs text-gray-500">Follow us:</span>
                <a href="#" className="text-gray-400 hover:text-black transition-colors" title="X">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H2.68l7.73-8.835L1.414 2.25H8.2l6.312 8.279-1.37 1.574Zm-1.5 1.516L6.732 3.74H5.064l6.865 8.981-1.5 1.716Z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-pink-600 transition-colors" title="Instagram">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 0 4 4 0 018 0zm4.965-10.405a1.68 1.68 0 112.36 2.364 1.68 1.68 0 01-2.36-2.364z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-green-600 transition-colors" title="WhatsApp">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.466 3.486"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
